import { Component, Input, ViewChild, TemplateRef, Output, EventEmitter, ViewEncapsulation, ViewContainerRef, HostListener, HostBinding } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { TimeoutQueue } from '@common/services/timeout-queue';
import { Subscription } from 'rxjs';
import { AssignmentRecord } from '@playbook/data-models/records/assignment-record';
import { ModalService } from '@common/components/modal/modal.service';
import { AppService } from '@suite/app.service';
import { PlaybookService } from '@playbook/services/playbook-service';
import { Loader } from '@loader/loader';
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { PST_PREFIX } from "@common/enums/constants";
import { LanguageSettings } from '@common/services/language-settings';

import { GlobalTablesService } from "@common/services/global-tables-service";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { ExternalActionsService } from "@common/services/external-actions-service";
import { VisualStateService } from "@common/services/visual-state-service";
import { LayoutState } from "@common/services/layout-state";
import { BasicContentService } from "@common/services/basic-content-service";
import { NavigationService } from '@common/services/navigation-service';
import { GroupKey } from '@suite/common/data-models/search-results/group-key';
import { EnpsRecordControl } from '@common/controls/enps-record-control/enps-record-control';
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { DateTime } from '@common/data-models/complex-types/date-time';
import { FieldDefValidations } from '@common/enums/enums';
import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { DraggableEnpsComponent } from '@common/components/base/draggable-enps-component';

@Component({
    selector: 'assignment',
    templateUrl: './assignment.html',
    styleUrls: ['./assignment.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        'class': 'st-assignment-item'
    }
})
export class Assignment extends DraggableEnpsComponent {

    public inView: boolean = false;
    public readonly FieldDefValidations = FieldDefValidations;

    private _data: AssignmentRecord = new AssignmentRecord();
    @Input()
    public get data(): AssignmentRecord {
        return this._data;
    }
    public set data(v: AssignmentRecord) {
        this._data = v;
        this._guid = v.guid;
        if (v) {
            this.editable = this._data.getSecurityPrivsWrite(this.appService);
            this.readable = this._data.getSecurityPrivsRead(this.appService);
            this.isReadOnly = this.layoutState.inSlideshowMode;
            if (this.data && this.data.path && (this.data.path.isWasteBinPath || this.data.parentRecord?.path?.isWasteBinPath)) {
                this.isReadOnly = true;
                this.editable = false;
            }

            if (this.recordChangedSubscription) this.recordChangedSubscription.unsubscribe();
            this.recordChangedSubscription = super.createSubscription(this._data.changeSubject, this.recordChanged.bind(this));
            this.parentRecordChangedSubscription = super.createSubscription(this._data?.parentRecord.changeSubject, this.parentRecordChanged.bind(this));
        }
    }

    private _isReadOnly: boolean = false;
    @Input()
    public get isReadOnly(): boolean {
        return this._isReadOnly || this.layoutState.inSlideshowMode;
    }
    public set isReadOnly(v: boolean) {
        if (!v && this.editable) {
            this._isReadOnly = v;
        } else if (v) {
            this._isReadOnly = v;
        }
    }

    @Input() public loader: Loader;
    @Input() public isPartOfCard: boolean = false;
    @Input() public isNewTopic: boolean = false;
    @Input() public isNewAssignment: boolean = false;
    @Input() public showExternalActions: boolean = true;
    @Input() public dragEnabled: boolean = true;
    @Input() public currentGroup: GroupKey;

    public editable: boolean = true;
    public readable: boolean = true;
    @Output() public assignmentOpened: EventEmitter<void> = new EventEmitter();
    @Output() public assignmentDeleted: EventEmitter<number> = new EventEmitter();

    private _isPublication: boolean = false;
    @Input()
    public get isPublication(): boolean {
        return this._isPublication;
    }
    public set isPublication(v: boolean) {
        this._isPublication = v;
    }
    @Output() public onDeletePublication: EventEmitter<any> = new EventEmitter();

    @ViewChild(CdkConnectedOverlay) overlay: CdkConnectedOverlay;
    @ViewChild('externalActions', { read: TemplateRef }) externalActions: TemplateRef<any>;

    @HostListener('click', ['$event'])
    onClick(event: MouseEvent) {
        if (this.visualStateService.isLessThanMedium || (this.inFullscreenMode && this.visualStateService.isLessThanXLarge)) {
            this.assignmentOpened.emit();
        }
    }

    @HostBinding('class.st-assignment-item--selected')
    @Input()
    selected: boolean = false;


    @HostBinding('class.st-assignment-item__mobile')
    get mobileCard(): boolean {
        return (this.visualStateService.isLessThanMedium || (this.inFullscreenMode && this.visualStateService.isLessThanXLarge));
    }

    @Input()
    get inFullscreenMode() {
        return this._inFullscreenMode;
    }
    set inFullscreenMode(value: BooleanInput) {
        this._inFullscreenMode = coerceBooleanProperty(value);
    }
    private _inFullscreenMode = false;

    public isMouseOverInOverlay: boolean = false;

    public get assignmentOwnerProperty(): string {
        return PSTFieldNames.PSTAssignmentOwner;
    }

    public get publishingDesk(): Array<string> {
        return this.data.department;
    }

    private _targetDate: string = null;
    get targetDate(): string {
        if (this._targetDate == null && this.data && this.data.fileDate && this.data.fileDate.get() && this.data.fileDate.get().getUTCFullYear() != 1800) {
            this._targetDate = this.data.fileDate.moment.format('MMMM Do - h:mmA');
        }
        return this._targetDate;
    }

    get assignmentsAttachmentsFiedGroupLabel() {
        const attachmentsFieldGroupLabel = 'PSTWebFieldGroup';
        return this.languageSettings.get(`${attachmentsFieldGroupLabel}_${this.languageSettings.PSTWebAttachmentsPane}`).includes(attachmentsFieldGroupLabel) ? this.languageSettings.PSTWebAttachmentsPane : this.languageSettings.get(`${attachmentsFieldGroupLabel}_${this.languageSettings.PSTWebAttachmentsPane}`)
    }

    get publicationDateLabel(): string {
        return this.data?.fileDate?.toISODateTimeString();
    }

    get timeUpdatedLabel(): string {
        return `${this.languageSettings.PSTWebChangedAgoLabel} ${this.data?.modTime?.moment?.fromNow()}`;
    }

    get updatedBy(): string {
        return `${this.languageSettings.PSTWebChangedByLabel} ${this.globalTablesService.Staff[this?.data?.modBy]?.DisplayName}`;
    }

    get showFullCard(): boolean {
        return !(this.visualStateService.isLessThanMedium || (this.inFullscreenMode && this.visualStateService.isLessThanXLarge));
    }

    get cardDisabled(): boolean {
        return this.isReadOnly || !this.editable;
    }

    get searchString(): string {
        return this.layoutState.viewStateSearchQuery;
    }

    get enforceRequiredFieldsOnCreate(): boolean {
        return this.globalTablesService.EnforceRequiredFieldsOnCreate
    }

    public hover: boolean = false;
    public recordChangesSub: Subscription;
    public dragData: string = '';
    private recordChangedSubscription: Subscription = null;
    private parentRecordChangedSubscription: Subscription = null;
    sub: any;

    public pstFieldNames = PSTFieldNames;
    assignmentEditable: boolean = true;
    isPanelOpen: boolean = false;

    constructor(
        public appService: AppService,
        public basicContentService: BasicContentService,
        public playbookService: PlaybookService,
        protected activatedRoute: ActivatedRoute,
        protected router: Router,
        public modalService: ModalService,
        timeoutQueue: TimeoutQueue,
        public languageSettings: LanguageSettings,
        public globalTablesService: GlobalTablesService,
        public nomWebServicesJson: NomWebServicesJson,
        public externalActionsService: ExternalActionsService,
        public visualStateService: VisualStateService,
        public layoutState: LayoutState,
        public navigationService: NavigationService,
        private vref: ViewContainerRef
    ) {
        super(timeoutQueue);
    }

    async ngOnInit() {
        setTimeout(() => {
            this.inView = true;
            if (this.isReadOnly || this.layoutState.inSlideshowMode) this.editable = false;
        }, 0);
    }

    async ngAfterViewInit() {
        setTimeout(() => {
            if (this.externalActions) {
                this.vref.createEmbeddedView(this.externalActions);
            }
        }, 0);
    }

    public recordChanged() {
        if (this.data) {
            this.editable = this.data.getSecurityPrivsWrite(this.appService);
            this.readable = this.data.getSecurityPrivsRead(this.appService);
            this._targetDate = null;
            this.itemStringCache.clear();
            if (this.data?.path?.isWasteBinPath || this.data?.parentRecord?.path?.isWasteBinPath) {
                this.editable = false;
                this.isReadOnly = true;
            }
        }
    }

    public parentRecordChanged() {
        if (this.data) {
            this._targetDate = null;
            this.itemStringCache.clear();
            if (this.data?.parentRecord?.path?.isWasteBinPath) {
                this.editable = false;
                this.isReadOnly = true;
            }
        }
    }

    public async onENPSObjectDrop(data: any) {
        if (data instanceof FileList) {
            let attachments = [];
            for (let i = 0; i < data.length; i++) {
                const shouldProceed = await this.appService.shouldProceedAttachment(data[i].name, data[i].size);
                if (!shouldProceed) return;
                let attachment = data[i];
                attachments.push({ file: attachment, finished: false, name: attachment.name });
            }
            let base64Files = await this.appService.readUrl(attachments);
            for (let file of base64Files) {
                try {
                    file.ExternalFiles = this.data.attachments;
                    let res = await this.appService.attachmentService.createAttachment(this.data.database, this.data.path.fullPath, this.data.guid, false, file, this.referalId)
                } catch (ex) { }
            }
        }
    }

    public dragStartHandler($event) {
        $event.dataTransfer.setData('text/plain', PST_PREFIX + JSON.stringify(this.data.getDataTransferString(this.appService)));
        $event.stopPropagation();
    }

    updateAssignment(value: any, fieldId: string) {
        if (fieldId === PSTFieldNames.PSTAssignmentSlug) {
            this.data.assignmentSlug = value.trim();
        }
        EnpsRecordControl.saveChanges(value, fieldId, this.data);
    }


    public getDateTimeValue(id: string): DateTime {
        return this.data.getAs<DateTime>(id, new DateTime());
    }

    //TODO: Probably needs to me util function
    public itemStringCache = new Map<string, string>();
    getItemString(itemID: string): string {
        const item = this.globalTablesService.FieldDefsDict[itemID];
        if (this.itemStringCache.has(item?.ID)) {
            return this.itemStringCache.get(item?.ID);
        }
        let str = "";
        const itemData = this.data.get(item?.ID, null);
        switch (item?.Validation?.toUpperCase()) {
            case FieldDefValidations.CHECKBOX: {
                str = itemData ? this.appService.languageSettings.get('PSTWebYesOption') : this.appService.languageSettings.get('PSTWebNoOption');
                break;
            }
            case FieldDefValidations.CHECKLISTEDIT:
            case FieldDefValidations.CHECKLIST: {
                if (itemData) {
                    let options = this.globalTablesService.getListItemsForField(item?.ID);
                    if (itemID == PSTFieldNames.PSTTopicDepartment) {
                        options = this.globalTablesService.getListItemsForDesk(this.data.type);
                    }
                    str = (itemData as MultiValue<string>).map((item) => {
                        return options.find((option) => option.id === item)?.title;
                    }).join(', ');
                }
                break;
            }
            case FieldDefValidations.STAFFIDLIST: {
                if (itemData) {
                    str = (itemData as MultiValue<string>).map((item) => {
                        return this.globalTablesService.Staff[item]?.DisplayName;
                    }).join(', ');
                }
                break;
            }
            case FieldDefValidations.COMBOBOX: {
                if (itemData) {
                    const options = this.globalTablesService.getListItemsForField(item?.ID);
                    str = options.find((option) => option.id === itemData)?.title;
                }
                break;
            }
            case FieldDefValidations.TIME: {
                if (itemData) {
                    str = (itemData as DateTime)?.toDateTimeString();
                }
                break;
            }
            case FieldDefValidations.DATE: {
                if (itemData) {
                    str = (itemData as DateTime)?.toDateString();
                }
                break;
            }
            default: {
                if (itemData) {
                    str = itemData?.toString() || '';
                }
                break;
            }
        }
        this.itemStringCache.set(item?.ID, str);
        return str;
    }

    public deletePublication(event: any) {
        event.stopPropagation();
        this.onDeletePublication.emit();
    }
}
